import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { timecode } from "../lib/scrub";

/**
 * A cut, shown to somebody who is not in the space and has no account
 * (ADR 000057).
 *
 * The only route in the studio that renders without a session, which is the
 * point: requiring an account from a client reviewing one cut is the thing a
 * share exists to avoid.
 *
 * It shows the project's name, its chain, and whatever has actually been
 * rendered. It cannot show anything else, because the endpoint behind it
 * cannot serve anything else: a shot arrives here as where it sits, what it is
 * called, whether it is made and the direction saying what it is, rather than
 * as the brief it was made from (ADR 000132).
 */
export function SharedRoute() {
  const { token = "" } = useParams();
  const [playing, setPlaying] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);

  const shared = useQuery({
    queryKey: ["shared", token],
    queryFn: () => api.openShare(token),
    retry: false,
  });

  if (shared.isPending) {
    return (
      <main className="shared" role="status">
        <p>Loading…</p>
      </main>
    );
  }

  // Unknown, revoked and expired all arrive here identically, which is
  // deliberate: a dead link says nothing about why it is dead.
  if (shared.error || !shared.data) {
    return (
      <main className="shared">
        <h1>This link does not work</h1>
        <p className="muted">
          It may have been turned off, or it may have expired. Whoever sent it to you can make a new
          one.
        </p>
      </main>
    );
  }

  const { projectName, spaceName, timeline, segments, renditions, mediaUrls, posterUrls } =
    shared.data;

  const renditionFor = (segmentId: string) =>
    renditions.find((rendition) => rendition.segmentId === segmentId);

  const watchable = segments.filter((segment) => {
    const rendition = renditionFor(segment.id);
    return rendition != null && mediaUrls[rendition.id] != null;
  });

  return (
    <main className="shared">
      <header>
        <p className="muted">{spaceName}</p>
        <h1>{projectName}</h1>
        {/*
         * Counted and measured, both off the chain. A declared stage beside
         * them was the word somebody last chose, and this reader is the one
         * with no way to tell the two apart (ADR 000067).
         */}
        <p className="muted">
          {watchable.length} of {segments.length} {segments.length === 1 ? "shot" : "shots"} made
          {timeline.durationSec > 0 &&
            ` · ${timecode(timeline.readyDurationSec)} of ${timecode(timeline.durationSec)}`}
        </p>
      </header>

      {playing && <video ref={video} className="shared-player" src={playing} controls autoPlay />}

      {segments.length === 0 ? (
        <p className="muted">Nothing has been planned in this yet.</p>
      ) : (
        <ol className="shared-chain">
          {segments.map((segment, index) => {
            const rendition = renditionFor(segment.id);
            const media = rendition ? mediaUrls[rendition.id] : undefined;
            const poster = rendition ? posterUrls[rendition.id] : undefined;

            return (
              <li key={segment.id} className={`shared-shot is-${segment.state}`}>
                <span className="shared-index">{index + 1}</span>

                {media ? (
                  <button type="button" className="shared-still" onClick={() => setPlaying(media)}>
                    {poster ? <img src={poster} alt="" /> : <span className="shared-blank" />}
                    <span className="shared-play" aria-hidden="true">
                      ▶
                    </span>
                    <span className="visually-hidden">
                      Play shot {index + 1}
                      {segment.label ? `, ${segment.label}` : ""}
                    </span>
                  </button>
                ) : (
                  <span className="shared-still is-empty" aria-hidden="true" />
                )}

                <span className="shared-body">
                  <strong>{segment.label || `Shot ${index + 1}`}</strong>
                  <span className="muted">
                    {timecode(segment.startSec)} to {timecode(segment.endSec)}
                    {!media && " · not made yet"}
                  </span>
                  {segment.direction && <span className="muted">{segment.direction}</span>}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      <footer className="muted">
        <p>
          You are watching a shared cut. You can see this one piece of work and nothing else, and
          nothing here is changed by anything you do.
        </p>
      </footer>
    </main>
  );
}
